import moment from 'moment-timezone'
import find from 'lodash/find'
import {
  nowRegExp,
  relativeDayRegExp, weekdayRegExp,
  timestampRegExp,
  isoStringRegExp,
  timeRegExp, dateTimeRegExp,
  americanDateTimeRegExp, russianDateTimeRegExp
} from './regExpRecipes'
import { tttime, TIMEZONES } from './tttime'

/**
 * Create a tttimestamp, auto detect the type of value
 * @param {string | number | Moment} value
 * @return {TTTime}
 */
export function parseTTTime (value) {
  // stringify non-string inputs
  if (typeof value === 'number') {
    return parseTTTime(String(value))
  }
  if (moment.isMoment(value)) {
    return parseTTTime(value.toISOString())
  }

  // timestamp, unix timestamp
  if (timestampRegExp.exec(value)) {
    if (new Date(parseInt(value)).getFullYear() === 1970) {
      return tttime(value, 'unix')
    } else {
      return tttime(value, 'timestamp')
    }
  }

  // iso string
  if (isoStringRegExp.exec(value)) {
    return tttime(value, 'isoString')
  }

  // time
  const timeMatch = timeRegExp.exec(value)
  if (timeMatch) {
    const groups = timeMatch.groups()
    const date = moment().format('YYYY-MM-DD')
    const time = applyPeriod(groups.time, groups.period)
    const timezone = findTimezone(groups.timezone)

    return tttime(concat(date, time), 'dateTime', timezone)
  }

  // date [time [timezone]]
  const dateTimeMatch = dateTimeRegExp.exec(value)
  if (dateTimeMatch) {
    const groups = dateTimeMatch.groups()
    const date = groups.date
    const time = applyPeriod(groups.time, groups.period)
    const timezone = findTimezone(groups.timezone)

    return tttime(concat(date, time), 'dateTime', timezone)
  }

  // american date [americanTime [timezone]]
  const americanMatch = americanDateTimeRegExp.exec(value)
  if (americanMatch) {
    const groups = americanMatch.groups()
    const timezone = findTimezone(groups.timezone)
    const value = concat(groups.americanDate, groups.time, groups.period)

    return tttime(value, 'americanDateTime', timezone)
  }

  // russian date [time [timezone]]
  const russianMatch = russianDateTimeRegExp.exec(value)
  if (russianMatch) {
    const groups = russianMatch.groups()
    const timezone = findTimezone(groups.timezone)
    const value = concat(groups.russianDate, groups.time, groups.period)

    return tttime(value, 'russianDateTime', timezone)
  }

  // now
  if (nowRegExp.exec(value)) {
    return tttime(String(Date.now()), 'timestamp')
  }

  // today, tomorrow, yesterday
  const relativeDayMatch = relativeDayRegExp.exec(value)
  if (relativeDayMatch) {
    const groups = relativeDayMatch.groups()
    const date = dateFromRelativeDay(groups.relativeDay)
    const time = applyPeriod(groups.time, groups.period)
    const timezone = findTimezone(groups.timezone)

    return tttime(concat(date, time), 'dateTime', timezone)
  }

  // weekday: Saturday, Monday, Mon, ...
  const weekdayMatch = weekdayRegExp.exec(value)
  if (weekdayMatch) {
    const groups = weekdayMatch.groups()
    const date = dateFromWeekday(groups.weekday, groups.which)
    const time = applyPeriod(groups.time, groups.period)
    const timezone = findTimezone(groups.timezone)

    return tttime(concat(date, time), 'dateTime', timezone)
  }

  // empty input
  if (value.trim().length === 0) {
    return tttime(String(Date.now()), 'timestamp')
  }

  // Hm, no clue. See whether moment.js can make sense of the input
  const m = moment(value)
  if (m.isValid()) {
    return tttime(String(m.valueOf()), 'timestamp')
  }

  // last fallback is interpreting as local time
  return tttime(value, 'dateTime')
}

/**
 * Find a timezone from a value. Value can be a case insensitive part of a timezone name.
 * Returns the timezone name if there is an unambiguous match. Else, returns undefined.
 *
 * Example:
 *
 *     findTimezone('amsterdam')  // returns 'Europe/Amsterdam'
 *     findTimezone('foo')        // returns undefined
 *
 * @param {string} search
 * @return {string | undefined} Returns the matching timezone or undefined
 */
export function findTimezone (search) {
  if (typeof search === 'string') {
    const normalized = search.toLowerCase().replace(/ /g, '_')

    const match = find(TIMEZONES, timezone => {
      return timezone.toLowerCase() === normalized
    })
    if (match) {
      return match
    }

    const partialMatches = TIMEZONES.filter(timezone => {
      return timezone.toLowerCase().indexOf(normalized) !== -1
    })
    if (partialMatches.length === 1) {
      return partialMatches[0]
    }
    if (partialMatches.length > 1) {
      const matches = partialMatches
          .map(m => m.replace(/_/g, ' '))
          .slice(0, 4)
          .join(', ')

      const more = (partialMatches.length > 4)
          ? `, (${partialMatches.length - 4} more...)`
          : ''

      throw new Error(`Ambiguous timezone "${search}", could match: ${matches}${more}`)
    }

    throw new Error(`Unknown timezone "${search}"`)
  }

  return undefined
}

/**
 * Test whether a given moment is on a specific day
 * @param {Moment} datetime
 * @param {String} day   A case insensitive string like "Saturday" or "Mon"
 * @return {boolean}
 */
export function hasWeekDay (datetime, day) {
  return datetime.format('dddd').toLowerCase() === day.toLowerCase() ||
      datetime.format('ddd').toLowerCase() === day.toLowerCase()
}

/**
 * Apply a time period AM/PM to a time string
 * @param {string} time    A string like '09:30:45'
 * @param {string} period  Can be 'AM' or 'PM'. Case insensitive.
 * @return {string} Returns time with period applied
 *                 (ie. 12 hours added in case of PM)
 */
export function applyPeriod (time, period) {
  if (time && period && period.toUpperCase() === 'PM') {
    const split = time.split(':')
    const hours = parseInt(split[0])
    if (hours < 12) {
      return (hours + 12) + ':' + split.slice(1).join(':')
    }
  }

  return time
}

/**
 *
 * @param {string} weekday    A weekday like 'Saturday' or 'Tue'. Case insensitive
 * @param {string} [which]    'last' or 'next'. Optional
 * @return {string}           Returns the corresponding date, formatted as 'YYYY-MM-DD'
 */
export function dateFromWeekday (weekday, which) {
  const last = which && which.toLowerCase() === 'last'
  const next = which && which.toLowerCase() === 'next'

  let date = moment().startOf('day')

  if (hasWeekDay(date, weekday)) {
    // the day is already correct. adjust one week if needed
    if (last) {
      date.subtract(7, 'days')
    }
    if (next) {
      date.add(7, 'days')
    }
  } else {
    // loop until we find the right day
    while (!hasWeekDay(date, weekday)) {
      date.add(last ? -1 : 1, 'day')
    }
  }

  return date.format('YYYY-MM-DD')
}

/**
 * Format a relative day
 * @param {string} relativeDay   Can be 'today', 'tomorrow' or 'yesterday'
 * @return {string}              Returns the corresponding date,
 *                               formatted as 'YYYY-MM-DD'
 */
export function dateFromRelativeDay (relativeDay) {
  if (relativeDay.toLowerCase() === 'tomorrow') {
    return moment().add(1, 'day').format('YYYY-MM-DD')
  }

  if (relativeDay.toLowerCase() === 'yesterday') {
    return moment().subtract(1, 'day').format('YYYY-MM-DD')
  }

  // we suppose relativeDay === 'today'
  return moment().format('YYYY-MM-DD')
}

/**
 * Join inputs separated by a space. undefined inputs will be ignored
 * @param {...string} args
 * @return {string}
 */
export function concat (...args) {
  return args.filter(arg => arg !== undefined).join(' ')
}



// WEBPACK FOOTER //
// ./src/tttime/parseTTTime.js